import type { UserAdministration } from '../interfaces/user-administration';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { IGridRequestBody, IGridResponseBody } from 'mt-ui-lib/interfaces';
import { KeycloakSessionService } from '../services/auth/keycloak-session.service';
import type { IUserGridItem } from '../interfaces/user';

interface RegistryRow {
  id: string;
  login: string;
  fullName: string;
  email: string;
  enabled: boolean;
  authorized: boolean;
  subsystems: string;
  lastLoginAt: string | null;
  deletedAt: string | null;
}
const fields: Record<string, string> = {
  isBlocked: 'blocked',
  isDeleted: 'deleted',
  isAccessAdmin: 'securityAdministrator',
  ipActual: 'activeNetworks',
  ipDeleted: 'deletedNetworks',
  lastAuthorizedDatetime: 'lastLoginAt',
  deleteDatetime: 'deletedAt',
};

@Injectable({ providedIn: 'root' })
export class UserRegistryApiService {
  private readonly http = inject(HttpClient);
  private readonly url = inject(KeycloakSessionService).iamUrl + '/users';
  search(body: IGridRequestBody) {
    const pageNumber = Math.max(1, body.page?.pageNumber ?? 1);
    const pageSize = Math.min(100, Math.max(1, body.page?.pageSize ?? 50));
    return this.http
      .post<{ items: RegistryRow[]; total: number }>(this.url + '/search', {
        first: (pageNumber - 1) * pageSize,
        max: pageSize,
        search: body.searchString,
        filters: (body.complexFilters ?? []).map((group) => ({
          operator: group.operator ?? 'and',
          filters: (group.filters ?? [])
            .filter(
              (filter) =>
                (filter.value != null && filter.value !== '') ||
                ['isNull', 'isNotNull'].includes(filter.criteria),
            )
            .map((filter) => ({
              field: fields[filter.field] ?? filter.field,
              operator: filter.criteria ?? 'equals',
              value: filter.value,
            })),
        })),
        order: [...(body.sortings ?? [])]
          .sort((a, b) => a.index - b.index)
          .map((sort) => ({
            field: fields[sort.field] ?? sort.field,
            direction: sort.direction,
          })),
      })
      .pipe(
        map((result): IGridResponseBody<IUserGridItem> => ({
          pageNumber,
          pageSize,
          totalElements: result.total,
          totalPages: Math.ceil(result.total / pageSize),
          items: result.items.map((row) => ({
            ...row,
            lastAuthorizedDatetime: row.lastLoginAt,
            deleteDatetime: row.deletedAt,
          })),
        })),
      );
  }
  details(userId: string) {
    return this.http.get<UserAdministration>(
      this.url + '/' + encodeURIComponent(userId) + '/administration',
    );
  }
  subsystems() {
    return this.http.get<{ id: string; name: string }[]>(
      this.url + '/subsystems',
    );
  }
}
